import React, { useState } from 'react';
import { Flex, Input, MantineRadius, Popover, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { Store } from '../../../codegen/graphql';
import { isEligibleAdvertisingZoneStore } from '../../../helpers/storeSelectionHelper';
import { requestComboboxOptionHoverClassName } from './requestComboboxClassNames';
import { StoreSelectionItem } from './StoreSelectionItem';

interface ZoneStoreSelectPopoverProps {
    eligibleZoneStores: Store[];
    selectedStoreIds: string[];
    onChange: (selectedStoreIds: string[]) => void;
    error?: string;
    radius?: MantineRadius;
    highestCostPriceStoreId?: string | null;
}

function radiusTopClass(radius: MantineRadius): string {
    switch (radius) {
        case 'xs':
            return 'rounded-t-sm';
        case 'lg':
            return 'rounded-t-lg';
        case 'xl':
            return 'rounded-t-xl';
        case 'md':
            return 'rounded-t-md';
        default:
            return 'rounded-t-sm';
    }
}

function radiusBottomClass(radius: MantineRadius): string {
    switch (radius) {
        case 'xs':
            return 'rounded-b-sm';
        case 'lg':
            return 'rounded-b-lg';
        case 'xl':
            return 'rounded-b-xl';
        case 'md':
            return 'rounded-b-md';
        default:
            return 'rounded-b-sm';
    }
}

function radiusClosedClass(radius: MantineRadius): string {
    switch (radius) {
        case 'xs':
            return 'rounded-sm';
        case 'lg':
            return 'rounded-lg';
        case 'xl':
            return 'rounded-xl';
        case 'md':
            return 'rounded-md';
        default:
            return 'rounded-sm';
    }
}

export function ZoneStoreSelectPopover({
    eligibleZoneStores,
    selectedStoreIds,
    onChange,
    error,
    radius = 'sm',
    highestCostPriceStoreId,
}: ZoneStoreSelectPopoverProps) {
    const [opened, setOpened] = useState(false);

    const allZoneStoresSelected =
        eligibleZoneStores.length > 0 &&
        selectedStoreIds.length === eligibleZoneStores.length;

    const handleToggle = (id: string, checked: boolean) => {
        if (checked) {
            onChange([...selectedStoreIds, id]);
        } else {
            onChange(selectedStoreIds.filter(sId => sId !== id));
        }
    };

    const handleTargetClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setOpened(current => !current);
    };

    let selectionSummaryText: string;
    if (allZoneStoresSelected) {
        selectionSummaryText = 'All stores selected';
    } else if (selectedStoreIds.length > 0) {
        selectionSummaryText = `${selectedStoreIds.length} stores selected`;
    } else {
        selectionSummaryText = 'Select one or more stores';
    }

    const triggerRadiusClass = opened
        ? `${radiusTopClass(radius)} rounded-b-none`
        : radiusClosedClass(radius);

    const dropdownRadiusClass = `${radiusBottomClass(radius)} rounded-t-none`;

    const activeBorderClass = error
        ? '!border-[var(--mantine-color-red-6)]'
        : '!border-primary';

    const openedTriggerBorderClass = opened
        ? `${activeBorderClass} !border-b-0`
        : '';

    const openedDropdownBorderClass = opened
        ? `${activeBorderClass} !border !border-t-0 -mt-px`
        : '!border-0';

    return (
        <Input.Wrapper
            label="Stores in Zone"
            size="sm"
            error={error}
            className="w-full min-w-0"
        >
            <Popover
                opened={opened}
                onChange={setOpened}
                width="target"
                position="bottom-start"
                offset={{ mainAxis: 0, crossAxis: 0, alignmentAxis: 0 }}
                withArrow={false}
                shadow="none"
                withinPortal
                zIndex={1100}
                hideDetached={false}
                trapFocus={false}
                middlewares={{ flip: false, shift: true, inline: false }}
                classNames={{
                    dropdown: `overflow-visible bg-white px-4 py-2 ${dropdownRadiusClass} shadow-[0_8px_16px_-4px_rgba(0,0,0,0.08)] ${openedDropdownBorderClass}`,
                }}
            >
                <Popover.Target>
                    <Input
                        size="sm"
                        radius={radius}
                        component="button"
                        type="button"
                        pointer
                        onClick={handleTargetClick}
                        aria-expanded={opened}
                        className="w-full min-w-0"
                        classNames={{
                            input: [
                                triggerRadiusClass,
                                'cursor-pointer',
                                opened ? 'relative z-[1]' : '',
                                openedTriggerBorderClass,
                            ].join(' '),
                        }}
                        rightSection={
                            <IconChevronDown
                                size={16}
                                stroke={1.75}
                                className={`text-[var(--mantine-color-gray-6)] transition-transform duration-150 ${
                                    opened ? 'rotate-180' : ''
                                }`}
                            />
                        }
                        rightSectionPointerEvents="none"
                    >
                        <Text
                            size="sm"
                            c={
                                selectedStoreIds.length === 0
                                    ? 'dimmed'
                                    : 'dark.7'
                            }
                            className="min-w-0 flex-1 truncate text-left leading-snug"
                        >
                            {selectionSummaryText}
                        </Text>
                    </Input>
                </Popover.Target>
                <Popover.Dropdown
                    onClick={event => event.stopPropagation()}
                    onMouseDown={event => event.stopPropagation()}
                >
                    <Flex
                        direction="column"
                        className="max-h-[260px] overflow-y-auto"
                    >
                        {eligibleZoneStores.map(store => {
                            const isLive =
                                isEligibleAdvertisingZoneStore(store);
                            return (
                                <div
                                    key={store.id}
                                    className={`py-1.5 ${
                                        isLive
                                            ? `cursor-pointer ${requestComboboxOptionHoverClassName}`
                                            : 'cursor-not-allowed'
                                    }`}
                                >
                                    <StoreSelectionItem
                                        store={store}
                                        isSelected={selectedStoreIds.includes(
                                            store.id,
                                        )}
                                        onToggle={handleToggle}
                                        isHighestCostPrice={
                                            store.id === highestCostPriceStoreId
                                        }
                                    />
                                </div>
                            );
                        })}
                    </Flex>
                </Popover.Dropdown>
            </Popover>
        </Input.Wrapper>
    );
}
